import { Topics } from '@app/context/constants';
import { useNavigation } from '@app/hooks/useNavigation';
import { useSnackbar } from '@app/hooks/useSnackbar';
import { useUserValuesData } from '@app/hooks/useUserValuesData';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { useDashboard } from '@app/routes/Dashboard/useDashboard';
import { copyToClipboard } from '@app/utils/copyToClipboard';
import { getTopicStates } from '@app/utils/getTopicStates';
import { AlertDialog, Loading } from '@components/ui';
import { UserValue } from '@dieterApi/persistentState/useUserValuesQuery';
import { useQuestionnaireDocumentShareQuery } from '@dieterApi/questionnaire/useQuestionnaireDocumentShareQuery';
import { User, UserQuestionnaireApplicationTopic, UserTopicApplications } from '@dieterApi/user/useUserQuery';
import { useUserValueAddMutation } from '@dieterApi/user/useUserValueAddMutation';
import { useUserValueDeleteMutation } from '@dieterApi/user/useUserValueDeleteMutation';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ShareIcon from '@mui/icons-material/Share';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Button, IconButton, Tooltip } from '@mui/material';
import { useEntryHighlight } from '@app/hooks/useEntryHighlight';
import MUIDataTable, { MUIDataTableMeta, MUIDataTableOptions, MUIDataTableProps } from 'mui-datatables';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useImmer } from 'use-immer';

export interface DSFATableMeta extends MUIDataTableMeta {
  rowData: [
    string, // index
    string, // description
    string | null, // createdAt
    UserQuestionnaireApplicationTopic | null,
  ];
}

interface Props {
  topic: UserTopicApplications;
  topicStates: ReturnType<typeof getTopicStates>;
}

export function DSFAList({ topic, topicStates }: Props): JSX.Element {
  const { t } = useTranslation();
  const { user, principalCompany, topics, questionnaires, questionnairesWithoutDpoFilter } = useUserWithCompany();
  const { userValues } = useUserValuesData();
  const dashboard = useDashboard();
  const {
    navigation: { roadBlockDSFA },
    setNavigation,
  } = useNavigation();
  const blankEntry = { index: '', description: '' };
  const [newEntry, setNewEntry] = useImmer<DSFAData>(blankEntry);
  const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState<DSFATableMeta | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [getShareToken, { data: tokenData, loading: shareLoading }] = useQuestionnaireDocumentShareQuery();

  const dsfaData = useMemo(() => {
    if (!userValues) return [];
    const parsedData = extractDSFAData(user, userValues, questionnairesWithoutDpoFilter);
    return newEntry.index ? [...parsedData, newEntry] : parsedData;
  }, [user, userValues, newEntry, questionnairesWithoutDpoFilter]);

  const [addUserValue] = useUserValueAddMutation();
  const [deleteUserValue] = useUserValueDeleteMutation();

  const handleRef = useCallback<(node: HTMLInputElement) => void>((node) => {
    if (node !== null) {
      node.focus();
    } else {
      window.focus();
    }
  }, []);

  const handleDelete = (tableMeta: DSFATableMeta) => {
    const index = tableMeta.rowData?.[0] as string;
    if (index === 'new') return;
    const keys = ['DSFA_DESCRIPTION'];
    keys.forEach((key) => deleteUserValue({ variables: { key, index }, context: { userId: user?.id || '' } }));
  };

  const handleAdd = () => {
    addUserValue({
      variables: { key: 'DSFA_DESCRIPTION', value: '', createIndex: true },
      // context: { userId: user?.id || '' },
    }).then((res) => {
      if (res.data?.addUserValue) {
        const app = topics.find((t) => t.id === Topics.DSFA)?.applications.find((a) => a.id === 'app-dsfa-create');
        app && dashboard.create(app.id, undefined, true, res.data.addUserValue.index);
      }
    });
  };

  const handleUpdate = (dsfa: DSFAData, description: string) => {
    addUserValue({
      variables: { key: 'DSFA_DESCRIPTION', value: description, index: dsfa.index },
    });
  };

  const handleOpenQuestionnaire = (meta: DSFATableMeta) => {
    dashboard.open(meta.rowData[3]!.id, undefined, true);
  };

  const handleShowContract = (
    meta: DSFATableMeta,
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    action: 'show' | 'download'
  ) => {
    if (topicStates.isLocked) {
      roadBlockDSFA.open();
    }
    const questionnaire = meta.rowData[3]!;
    setNavigation((nav) => {
      nav.documentPaper = {
        modalOpen: true,
        questionnaire,
        index: 0,
      };
    });
  };

  const handleShare = async (questionnaire: UserQuestionnaireApplicationTopic) => {
    try {
      await getShareToken({
        variables: {
          localQuestionnaireId: questionnaire.id,
          type: 'ListQuestionnaire',
          withConfirmation: false,
        },
      }).then(async (res) => {
        const token = res.data?.getQuestionnaireShareToken;
        if (token) {
          await copyToClipboard(window.location.origin + '/share?token=' + token);
          enqueueSnackbar('Link kopiert!', { variant: 'success' });
        } else {
          enqueueSnackbar('Fehler beim Generieren des Links.', { variant: 'error' });
        }
      });
    } catch (e) {
      enqueueSnackbar('Fehler beim Kopieren.', { variant: 'error' });
    }
  };

  const { rowProps } = useEntryHighlight();

  const columns: MUIDataTableProps['columns'] = [
    {
      name: 'index',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'description',
      label: 'Beschreibung',
      options: {
        customBodyRender: (value, tableMeta, updateValue) => {
          const index = tableMeta.rowData?.[0];
          const isNew = tableMeta.rowData?.[0] === 'new';
          const dsfa = dsfaData.find((i) => i.index === index);
          return (
            <div>
              <input
                ref={isNew ? handleRef : null}
                className="w-full bg-transparent text-sm"
                placeholder="Kurzbeschreibung"
                type="text"
                value={value}
                onChange={(e) => {
                  updateValue(e.target.value);
                }}
                onBlur={(e) => {
                  updateValue(e.target.value);
                  handleUpdate(dsfa!, e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.blur();
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.blur();
                  }
                }}
              />
            </div>
          );
        },
      },
    },
    {
      name: 'createdAt',
      label: 'Erfasst am',
      options: {
        customBodyRender: (value) => {
          return value ? new Date(value).toLocaleDateString() : '';
        },
      },
    },
    {
      name: 'questionnaire',
      options: {
        display: 'excluded',
      },
    },
    {
      name: 'actions',
      label: 'Aktionen',
      options: {
        customBodyRender: (value, tableMeta, updateValue) => {
          const meta = tableMeta as DSFATableMeta;
          const index = meta.rowData[0];
          const questionnaire = meta.rowData[3];
          const isNotFinished = (questionnaire?.progress || 0) < 1;

          const canShare = questionnaire && (principalCompany?.isDpo || principalCompany?.isAgency);
          if (isNotFinished) {
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Link to="#" onClick={() => handleOpenQuestionnaire(meta)}>
                  Fortsetzen
                </Link>
                {canShare && (
                  <Tooltip title="Fragebogen teilen">
                    <IconButton size="small" onClick={() => handleShare(questionnaire)}>
                      <ShareIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            );
          } else {
            return (
              <>
                <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title="Anzeigen">
                  <IconButton size="small" onClick={(e) => handleShowContract(meta, e, 'show')}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title="Bearbeiten">
                  <IconButton size="small" onClick={(e) => handleOpenQuestionnaire(meta)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            );
          }
        },
        customHeadLabelRender: () => '',
      },
    },
    {
      name: 'delete',
      label: 'Löschen',
      options: {
        customBodyRender: (value, tableMeta, updateValue) => {
          const meta = tableMeta as DSFATableMeta;
          const index = meta.rowData[0];
          const isNew = index === 'new';
          return (
            <div className="z-30">
              <Tooltip enterTouchDelay={0} leaveTouchDelay={5000} title={isNew ? 'Speichern' : 'DSFA löschen'}>
                <IconButton
                  className="text-gray-500 hover:text-danger"
                  size="small"
                  onClick={() => {
                    if (isNew) {
                      // handleSave(description);
                    } else {
                      setDeleteRow(meta);
                      setDeleteAlertOpen(true);
                    }
                  }}
                  data-testid={`buton-action-dsfaList-${meta.rowIndex}`}
                >
                  {isNew ? <SaveIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </div>
          );
        },
        customHeadLabelRender: () => '',
      },
    },
  ];

  const options: MUIDataTableOptions = {
    setRowProps: (row) => rowProps(row[0] as string),
    sortOrder: {
      name: 'name',
      direction: 'asc',
    },
    download: false,
    print: false,
    elevation: 0,
    selectableRows: 'none',
    selectableRowsHeader: false,
    customToolbar: () => (
      <Button variant="outlined" onClick={handleAdd} startIcon={<AddIcon />} disabled={user?.isReadOnly} size="small">
        Neue DSFA erstellen
      </Button>
    ),
    search: false,
    filter: false,
    viewColumns: false,
    pagination: false,
    textLabels: {
      selectedRows: {
        text: 'Zeilen ausgewählt',
        delete: 'Löschen',
        deleteAria: 'Ausgewählte Zeilen löschen',
      },
      body: {
        noMatch: 'Keine Einträge vorhanden',
      },
    },
  };

  return (
    <div className="mb-5">
      {dsfaData ? <MUIDataTable columns={columns} data={dsfaData} title="Deine DSFA" options={options} /> : <Loading />}
      <AlertDialog
        open={deleteAlertOpen}
        setOpen={setDeleteAlertOpen}
        message={
          <>
            Möchtest du die DSFA <b>{deleteRow?.rowData[1]}</b> wirklich löschen?
          </>
        }
        onAccept={() => deleteRow && handleDelete(deleteRow)}
      />
    </div>
  );
}

interface DSFAData {
  index: string;
  description: string;
  questionnaire?: UserQuestionnaireApplicationTopic;
  createdAt?: string;
}

const extractDSFAData = (
  user: User | undefined,
  userValues: UserValue[],
  questionnaires: any[] | undefined
): DSFAData[] => {
  if (!user) return [];
  const dsfaData: DSFAData[] = [];
  userValues.forEach((userValue) => {
    if (userValue.key === 'DSFA_DESCRIPTION') {
      const description = userValue.value;
      const questionnaire = questionnaires?.find(
        (q) => q.userValueIndex === userValue.index && q.application.id === 'app-dsfa-create'
      );
      dsfaData.push({
        description,
        index: userValue.index,
        questionnaire,
        createdAt: userValue.createdAt,
      });
    }
  });
  return dsfaData;
};
